/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting, SortDirection, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { IPopup } from 'app/shared/elements/popup-base';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import { bindable } from 'aurelia-typed-observable-plugin';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { MemberService } from 'app/cd/services/member-service';
import { LookupService } from 'app/core/services/lookup-service';
import { apiCall } from 'app/shared/lib/api/api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { deepClone } from 'app/shared/lib/utilities';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class Contacts extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    @bindable.boolean()
    protected showHouseholdFields: boolean = false;

    protected editedContact: CD.MemberContact = {};

    protected relationships: ReadonlyArray<CD.MemberRelationship> = [];
    protected types: ReadonlyArray<CD.MemberContactType> = [];
    protected regions: ReadonlyArray<Core.AddressRegion> = [];

    protected validateForm = validate;
    protected saveContactIsValid: boolean = false;

    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;
    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;

    protected memberContacts: CD.MemberContactComposite[] = [];
    protected allMemberContacts: CD.MemberContactComposite[] = [];
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected systemCodes: {
        [key: string]: string
    } = {};


    ////****************** Element References ***************************************////
    private $saveContactModal!: IPopup;
    protected $saveContactForm!: IForm;
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

        this.checkPermissions();
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await Promise.all([
            this.loadMemberContactsComposite(),
            this.loadAllMembersContacts(),
            this.loadLookups()
        ]);
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        await this.loadMemberContactsComposite();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
    }

    protected closeSaveContactModal(): void {
        if(this.$saveContactForm.reset) {
            this.$saveContactForm.reset();
        }
        this.$saveContactModal.close();
    }

    protected async handleSaveContactFormSuccess(): Promise<void> {
        this.closeSaveContactModal();
        await Promise.all([
            this.loadMemberContactsComposite(),
            this.loadAllMembersContacts()
        ]);
    }

    protected saveContact(contact: CD.MemberContact | null = null): void {
        this.editedContact = contact ? deepClone(contact) : {};
        this.$saveContactModal.open();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberContactsComposite(): Promise<void> {
        if(!this.memberId) return;

        // insist on default 'name.full' sort
        if(!this.sorting.entries.find(entry => entry.reference === 'name.full')) {
            this.sorting.add({
                reference: 'name.full',
                direction: SortDirection.Ascending
            });
        }

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [
            'memberContact',
            'memberContact.types',
            'memberContact.phoneNumber.home',
            'memberContact.phoneNumber.cell',
            'memberContact.phoneNumber.work',
            'memberContact.phoneNumber.fax',
            'memberContact.address.mailing',
            'memberContact.relatedMember',
            'memberContact.relatedMember.phoneNumber.home',
            'memberContact.relatedMember.phoneNumber.cell',
            'memberContact.relatedMember.phoneNumber.work',
            'memberContact.relatedMember.phoneNumber.fax',
            'memberContact.relatedMember.address.mailing',
            'memberProvider',
            'memberProvider.provider',
            'memberProvider.provider.location',
            'memberProvider.provider.location.facility.phoneNumber.primary',
            'memberProvider.provider.location.facility.phoneNumber.secondary',
            'memberProvider.provider.location.facility.phoneNumber.fax',
            'memberProvider.provider.location.facility.address.physical'
        ];

        try {
            this.state.beginLoad();
            let response = await this.memberService.loadMemberContactsComposite(this.memberId, options, includes);
            this.memberContacts = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateContact(contact: CD.MemberContactComposite): Promise<void> {
        if(!contact.memberContact?.id) return;

        // Show confirmation dialog only if deactivating
        if(contact.memberContact.isActive) {
            const result = await confirmation.showDeactivate('cd:memberContact.$', 1, false);
            if(!(result.dialogResponse === confirmation.options.OK)) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.memberService.activateMemberContact(contact.memberContact.id, !contact.memberContact.isActive);
            isSuccessful = response.isSuccessful;
            await this.loadMemberContactsComposite();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('cd:memberContact.$', !contact.memberContact.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAllMembersContacts(): Promise<void> {
        if(!this.memberId) return;

        try {
            this.state.beginLoad();

            const options: StandardLoadOptions = {
                includeInactive: true
            };

            const includes: string[] = [
                'memberContact.relatedMember'
            ];

            const response = await this.memberService.loadMemberContactsComposite(this.memberId, options, includes);
            this.allMemberContacts = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const [relationships, types, regions, addressTypes, phoneNumberTypes] = await Promise.all(
                [
                    this.memberService.loadMemberRelationships(),
                    this.memberService.loadMemberContactTypes(),
                    this.lookupService.loadStates(),
                    this.memberService.loadMemberContactAddressTypes(),
                    this.memberService.loadMemberContactPhoneNumberTypes()
                ]);

            this.relationships = relationships.results;
            this.types = types.results;
            this.regions = regions.results;
            // Aggregate address types and phone number types, pairing them to their IDs
            ([...addressTypes.results, ...phoneNumberTypes.results] as (CD.MemberAddressType | CD.MemberPhoneNumberType)[]).forEach(type => {
                if(type.systemCode && type.id) {
                    this.systemCodes[type.systemCode] = type.id;
                }
            });
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
